<template>
  <div class="max-w-6xl h-full m-auto py-4 px-4 flex flex-col space-y-4">
    <div class="flex flex-row justify-between space-x-4 py-2">
      <h2 class="text-2xl">Fraud Orders Report</h2>

      <div class="flex space-x-4 items-center">
        <Button
          v-on:click-handler="exportCSV()"
          class="btn btn-primary btn-small flex space-x-2 items-center"
          :disabled="isLoading || !gridHasData"
        >
          <span>Download CSV</span>
          <icon set="outline" name="document-download" class="h-5 w-5" />
        </Button>
        <Button
          v-on:click-handler="loadReport()"
          class="btn btn-primary btn-small flex space-x-2 items-center"
          :disabled="isLoading"
        >
          <span>Refresh Report</span>
          <LoadingSpinner v-if="isLoading" class="h-5 w-5" />
        </Button>
      </div>
    </div>

    <ag-grid-vue
      style="height: 100%; width: 100%"
      class="ag-theme-balham"
      :columnDefs="columnDefs"
      :rowData="rowData"
      :paginationAutoPageSize="false"
      :paginationPageSize="paginationPageSize"
      :pagination="true"
      id="tasks-report"
      :gridOptions="gridOptions"
      @grid-ready="onGridReady"
      :context="context"
      :frameworkComponents="frameworkComponents"
      :defaultColDef="defaultColDef"
      :enableCellTextSelection="true"
      :ensureDomOrder="true"
    >
    </ag-grid-vue>
  </div>
</template>
<script>
import { AgGridVue } from "ag-grid-vue";
import OrderLink from "./components/OrderLink";
import ShopifyOrderLink from "./components/ShopifyOrderLink";
import { ApiFactory } from "@/api/ApiFactory.js";
import dateFormatter from "@/mixins/dateFormatter";
import Button from "@/components/elements/Button.vue";
import IgnoreFraudScore from "./components/IgnoreFraudScore";
import moneyFormatter from "@/mixins/moneyFormatter";
import { date } from "@/components/agGrid/cellRenderers";

const Reports = ApiFactory.get("reports");

export default {
  name: "FraudOrders",
  components: {
    Button,
    AgGridVue,
    OrderLink, // eslint-disable-line
    ShopifyOrderLink, // eslint-disable-line
    IgnoreFraudScore, // eslint-disable-line
  },
  mixins: [dateFormatter, moneyFormatter],
  props: {},
  data() {
    return {
      columnDefs: null,
      rowData: null,
      paginationPageSize: 100,
      frameworkComponents: null,
      gridApi: null,
      columnApi: null,
      gridOptions: null,
      context: null,
      defaultColDef: null,
      overlayLoadingTemplate: true,
      isLoading: false,
    };
  },
  computed: {
    gridHasData() {
      return this.rowData && this.rowData.length;
    },
  },
  beforeMount() {
    this.gridOptions = {
      headerHeight: 60,
    };

    this.context = { componentParent: this };

    this.defaultColDef = {
      sortable: true,
      filter: true,
      flex: 1,
      suppressMovable: true,
      resizable: true,
    };

    this.columnDefs = [
      {
        headerName: "Fraud Score",
        field: "FraudScore",
        valueGetter: (params) => {
          let fraudScore = Number(params.data.FraudScore).toFixed(2);
          if (fraudScore == 1) {
            return "High";
          } else if (fraudScore >= 0.1) {
            return "Medium";
          }
          return "Low";
        },
      },
      {
        headerName: "Order Number",
        field: "SourceOrderNumber",
        cellRendererFramework: "OrderLink",
        cellRendererParams: {
          target: "_blank",
        },
      },
      {
        headerName: "Shopify",
        field: "SourceOrderID",
        cellRendererFramework: "ShopifyOrderLink",
        cellRendererParams: {
          target: "_blank",
        },
      },
      {
        headerName: "Date",
        field: "OrderTime",
        valueGetter: (params) =>
          new Date(params.data.OrderTime).setHours(0, 0, 0, 0),
        cellRenderer: "dateCell",
        filter: "agDateColumnFilter",
      },
      {
        headerName: "Status",
        field: "Status",
      },
      {
        headerName: "Name",
        field: "Name",
      },
      {
        headerName: "Grand Total",
        field: "FinalGrandTotal",
        valueGetter: (params) => Number(params.data.FinalGrandTotal),
        cellRenderer: (params) => moneyFormatter.format(params.value),
        filter: "agNumberColumnFilter",
        filterParams: {
          suppressAndOrCondition: true,
        },
      },
    ];
    this.frameworkComponents = {
      dateCell: date,
    };
    this.loadReport();
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
  },
  methods: {
    loadReport() {
      this.isLoading = true;
      this.gridOptions?.api?.showLoadingOverlay();

      Reports.getFraudOrders()
        .then((response) => (this.rowData = response.data.body))
        .finally(() => (this.isLoading = false));
    },
    exportCSV() {
      if (!this.gridHasData) return;
      function formattingFunction(params) {
        return params.column.getColId() === "OrderTime" && params.value
          ? new Date(params.value).toISOString().substr(0, 10)
          : params.value;
      }
      this.gridApi.exportDataAsCsv({
        fileName: this.$options.name + "-" + dateFormatter.format(new Date()),
        processCellCallback: formattingFunction,
      });
    },
    onFirstDataRendered(params) {
      params.api.sizeColumnsToFit();
    },
    onGridReady() {},
  },
};
</script>
<style lang="scss">
@import "../../../node_modules/ag-grid-community/dist/styles/ag-grid.css";
@import "../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";

.ag-header-cell-label .ag-header-cell-text {
  text-align: center;
  white-space: normal !important;
}
</style>
